import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DIAM_API_PATH,
  DIAM_PAGE_LIMIT,
  DiamRoleFilter,
  diamQueryKeys,
  getRoleById,
  getRolesPage,
  getTeamsPage,
} from '../../diamApi';
import { useApiEssentials } from '../../../../shared/hooks/useApiEssentials';
import { SelectOption } from '../CreateTeamDrawer/types';
import { TEAMS_LIST_FILTER } from '../TeamsGrid/useTeams';
import { mapCreateRoleContentsTree } from './mapCreateRoleContentsTree';

const FUNCTIONAL_ROLES_FILTER: DiamRoleFilter = { type: 'FUNCTIONAL' };

async function loadFunctionalRolesWithDetails(apiUrl: string, idToken: string) {
  const page = await getRolesPage(apiUrl, idToken, {
    page: 0,
    limit: DIAM_PAGE_LIMIT,
    filter: FUNCTIONAL_ROLES_FILTER,
  });

  const roles = page.elements ?? [];

  return Promise.all(
    roles.map(async role => {
      const hasPermissionData =
        (role.attributes?.length ?? 0) > 0 ||
        (role.inclusions?.length ?? 0) > 0;

      if (hasPermissionData) {
        return role;
      }

      return getRoleById(apiUrl, idToken, role.roleId);
    }),
  );
}

export function useCreateRoleOptions(isOpen: boolean) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials(DIAM_API_PATH);

  const teamsQuery = useQuery({
    queryKey: [
      ...diamQueryKeys.teams(TEAMS_LIST_FILTER),
      'create-role',
    ] as const,
    queryFn: async () => {
      const idToken = await getIdToken();
      return getTeamsPage(apiUrl, idToken, {
        page: 0,
        limit: DIAM_PAGE_LIMIT,
        filter: TEAMS_LIST_FILTER,
      });
    },
    enabled: isSignedIn && isOpen,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const functionalRolesQuery = useQuery({
    queryKey: [
      ...diamQueryKeys.roles(FUNCTIONAL_ROLES_FILTER),
      'create-role',
    ] as const,
    queryFn: async () => {
      const idToken = await getIdToken();
      return loadFunctionalRolesWithDetails(apiUrl, idToken);
    },
    enabled: isSignedIn && isOpen,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const teamOptions = useMemo<SelectOption[]>(
    () =>
      (teamsQuery.data?.elements ?? [])
        .map(team => ({
          label: team.name,
          value: team.id,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [teamsQuery.data],
  );

  const functionalRoles = useMemo(
    () => functionalRolesQuery.data ?? [],
    [functionalRolesQuery.data],
  );

  const contentsTree = useMemo(
    () => mapCreateRoleContentsTree(functionalRoles),
    [functionalRoles],
  );

  return {
    teamOptions,
    contentsTree,
    functionalRoles,
    isLoadingTeams: teamsQuery.isLoading,
    isLoadingContents: functionalRolesQuery.isLoading,
    isErrorTeams: teamsQuery.isError,
    isErrorContents: functionalRolesQuery.isError,
    teamsError: teamsQuery.error,
    contentsError: functionalRolesQuery.error,
  };
}
